import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { inmuebleService, allProductsService } from '../../services/inmuebleService';
import { Inmueble, ProductSummary } from '../../types/Inmueble';
import DateInput from '../common/DateInput';
import './InmuebleForm.css';

interface InmuebleFormProps {
  inmueble: Inmueble | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const emptyForm: Inmueble = {
  idProyecto: '',
  direccion: '',
  cp: '',
  poblacion: '',
  entidad: '',
  superficieM2: '',
  clase: '',
  anioConstruccion: '',
  costeInversion: '',
  fechaAdquisicion: '',
  valorMercadoActual: '',
  fechaActualizacion: '',
  estado: '',
  honorariosAdministracionFincas: '',
  estadoOcupacion: '',
  inquilino: '',
  fechaCttoAlquiler: '',
  fechaVtoCttoAlquiler: '',
  alquilerPrevAnual: '',
  comunidadPrevAnual: '',
  seguroPrevAnual: '',
  ibiPrevAnual: '',
  suministrosPrevAnual: '',
  otrosPrevAnual: '',
};

const toNum = (v: number | string | undefined): number => {
  if (v === '' || v === undefined || v === null) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const fmt = (v: number): string => {
  if (v === 0) return '-';
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const fmtPct = (v: number): string => {
  if (v === 0) return '-';
  return `${v.toFixed(2)} %`;
};

const InmuebleForm: React.FC<InmuebleFormProps> = ({ inmueble, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<Inmueble>(emptyForm);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    allProductsService.getAll().then((r) => setProducts(r.data)).catch(console.error);
    if (inmueble) {
      setFormData({ ...emptyForm, ...inmueble });
    } else {
      setFormData(emptyForm);
    }
  }, [inmueble]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Campos calculados en tiempo real
  const alquiler = toNum(formData.alquilerPrevAnual);
  const comunidad = toNum(formData.comunidadPrevAnual);
  const seguro = toNum(formData.seguroPrevAnual);
  const ibi = toNum(formData.ibiPrevAnual);
  const suministros = toNum(formData.suministrosPrevAnual);
  const otros = toNum(formData.otrosPrevAnual);
  const resultado = alquiler - comunidad - seguro - ibi - suministros - otros;
  const coste = toNum(formData.costeInversion);
  const mercado = toNum(formData.valorMercadoActual);
  const rtoBrutoAdq = coste > 0 ? (alquiler / coste) * 100 : 0;
  const rtoNetoAdq = coste > 0 ? (resultado / coste) * 100 : 0;
  const rtoBrutoMer = mercado > 0 ? (alquiler / mercado) * 100 : 0;
  const rtoNetoMer = mercado > 0 ? (resultado / mercado) * 100 : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (inmueble?.id) {
        await inmuebleService.update(inmueble.id, formData);
        showToast('Inmueble actualizado correctamente');
      } else {
        await inmuebleService.create(formData);
        showToast('Inmueble creado correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el inmueble');
    } finally {
      setLoading(false);
    }
  };

  const estadoOcupacionOptions = [
    { value: 'Vacío', label: 'Vacío' },
    { value: 'Alquilado (Renta mercado)', label: 'Alquilado (Renta mercado)' },
    { value: 'Alquilado (Renta antigua)', label: 'Alquilado (Renta antigua)' },
    { value: 'Ocupado', label: 'Ocupado' },
  ];

  return (
    <div className="inmueble-form">
      <h3>{inmueble ? 'Editar Inmueble' : 'Nuevo Inmueble'}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>

        {/* ── IDENTIFICACIÓN ─────────────────────────────────── */}
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="idProyecto">Proyecto</label>
            <select id="idProyecto" name="idProyecto" value={formData.idProyecto || ''} onChange={handleChange}>
              <option value="">Sin proyecto asignado</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id} — {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="clase">Clase</label>
            <select id="clase" name="clase" value={formData.clase || ''} onChange={handleChange}>
              <option value="">Seleccionar...</option>
              <option value="Urbano">Urbano</option>
              <option value="Comercial">Comercial</option>
              <option value="Almacenamiento">Almacenamiento</option>
              <option value="Párking">Párking</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="direccion">Dirección</label>
            <textarea
              id="direccion"
              name="direccion"
              value={formData.direccion || ''}
              onChange={handleChange}
              rows={2}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="cp">Código Postal</label>
            <input type="text" id="cp" name="cp" value={formData.cp || ''} onChange={handleChange} maxLength={10} />
          </div>
          <div className="form-group">
            <label htmlFor="poblacion">Población</label>
            <input type="text" id="poblacion" name="poblacion" value={formData.poblacion || ''} onChange={handleChange} maxLength={100} />
          </div>
          <div className="form-group">
            <label htmlFor="entidad">Entidad</label>
            <input type="text" id="entidad" name="entidad" value={formData.entidad || ''} onChange={handleChange} maxLength={200} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="superficieM2">Superficie (m²)</label>
            <input
              type="number"
              id="superficieM2"
              name="superficieM2"
              value={formData.superficieM2 || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="anioConstruccion">Año construcción</label>
            <input
              type="number"
              id="anioConstruccion"
              name="anioConstruccion"
              value={formData.anioConstruccion || ''}
              onChange={handleChange}
              min="1800"
              max="2100"
            />
          </div>
        </div>

        {/* ── INVERSIÓN ──────────────────────────────────────── */}
        <h4>Inversión</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="costeInversion">Coste de inversión (€)</label>
            <input
              type="number"
              id="costeInversion"
              name="costeInversion"
              value={formData.costeInversion || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaAdquisicion">Fecha adquisición</label>
            <DateInput
              id="fechaAdquisicion"
              name="fechaAdquisicion"
              value={formData.fechaAdquisicion || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="estado">Estado</label>
            <select id="estado" name="estado" value={formData.estado || ''} onChange={handleChange}>
              <option value="">Seleccionar...</option>
              <option value="Prospecto">Prospecto</option>
              <option value="Estudio">Estudio</option>
              <option value="Negociación">Negociación</option>
              <option value="Activo">Activo</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="valorMercadoActual">Valor de mercado actual (€)</label>
            <input
              type="number"
              id="valorMercadoActual"
              name="valorMercadoActual"
              value={formData.valorMercadoActual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaActualizacion">Fecha de actualización</label>
            <DateInput
              id="fechaActualizacion"
              name="fechaActualizacion"
              value={formData.fechaActualizacion || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="honorariosAdministracionFincas">Honorarios adm. fincas (%)</label>
            <input
              type="number"
              id="honorariosAdministracionFincas"
              name="honorariosAdministracionFincas"
              value={formData.honorariosAdministracionFincas || ''}
              onChange={handleChange}
              min="0"
              max="100"
              step="0.01"
            />
          </div>
        </div>

        {/* ── ESTADO DE OCUPACIÓN ────────────────────────────── */}
        <h4>Estado de ocupación</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="estadoOcupacion">Estado de ocupación</label>
            <select id="estadoOcupacion" name="estadoOcupacion" value={formData.estadoOcupacion || ''} onChange={handleChange}>
              <option value="">Seleccionar...</option>
              {estadoOcupacionOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group span-2">
            <label htmlFor="inquilino">Inquilino</label>
            <input type="text" id="inquilino" name="inquilino" value={formData.inquilino || ''} onChange={handleChange} maxLength={200} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaCttoAlquiler">Fecha ctto. alquiler</label>
            <DateInput
              id="fechaCttoAlquiler"
              name="fechaCttoAlquiler"
              value={formData.fechaCttoAlquiler || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaVtoCttoAlquiler">Fecha vto. ctto. alquiler</label>
            <DateInput
              id="fechaVtoCttoAlquiler"
              name="fechaVtoCttoAlquiler"
              value={formData.fechaVtoCttoAlquiler || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* ── PREVISIÓN DE INGRESOS Y GASTOS ─────────────────── */}
        <h4>Previsión de ingresos y gastos (anual)</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="alquilerPrevAnual">Alquiler prev. anual (€)</label>
            <input
              type="number"
              id="alquilerPrevAnual"
              name="alquilerPrevAnual"
              value={formData.alquilerPrevAnual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="comunidadPrevAnual">Comunidad prev. anual (€)</label>
            <input
              type="number"
              id="comunidadPrevAnual"
              name="comunidadPrevAnual"
              value={formData.comunidadPrevAnual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="seguroPrevAnual">Seguro prev. anual (€)</label>
            <input
              type="number"
              id="seguroPrevAnual"
              name="seguroPrevAnual"
              value={formData.seguroPrevAnual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ibiPrevAnual">IBI prev. anual (€)</label>
            <input
              type="number"
              id="ibiPrevAnual"
              name="ibiPrevAnual"
              value={formData.ibiPrevAnual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="suministrosPrevAnual">Suministros prev. anual (€)</label>
            <input
              type="number"
              id="suministrosPrevAnual"
              name="suministrosPrevAnual"
              value={formData.suministrosPrevAnual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="otrosPrevAnual">Otros prev. anual (€)</label>
            <input
              type="number"
              id="otrosPrevAnual"
              name="otrosPrevAnual"
              value={formData.otrosPrevAnual || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {/* Calculados */}
        <div className="form-row">
          <div className="form-group">
            <label>Resultado prev. anual (€)</label>
            <input type="text" className="readonly-calc" readOnly value={fmt(resultado)} />
          </div>
          <div className="form-group">
            <label>Rto. bruto prev. s/ adquisición</label>
            <input type="text" className="readonly-calc" readOnly value={fmtPct(rtoBrutoAdq)} />
          </div>
          <div className="form-group">
            <label>Rto. neto prev. s/ adquisición</label>
            <input type="text" className="readonly-calc" readOnly value={fmtPct(rtoNetoAdq)} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Rto. bruto prev. s/ valor de mercado</label>
            <input type="text" className="readonly-calc" readOnly value={fmtPct(rtoBrutoMer)} />
          </div>
          <div className="form-group">
            <label>Rto. neto prev. s/ valor de mercado</label>
            <input type="text" className="readonly-calc" readOnly value={fmtPct(rtoNetoMer)} />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : inmueble ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InmuebleForm;
